import React from 'react'
// libs
import { useMutation, useQuery } from '@apollo/client/react'
// graphql
import { API_CLIENTS_LIST } from 'graphql/apiClient/queries'
import { API_CLIENT_CREATE, API_CLIENT_REVOKE, API_CLIENT_UPDATE } from 'graphql/apiClient/mutations'
// constants
import { INITIAL_PER_PAGE } from 'constants/table'
// types
import {
  CreateApiClientInput,
  IApiClientCreatedCredentials,
  IApiClientsContext,
  UpdateApiClientInput
} from 'types/graphql'
// utils
import get from 'lodash/get'

type Props = React.PropsWithChildren<unknown>

export const ApiClientsPageContext = React.createContext<IApiClientsContext>({} as IApiClientsContext)

const ApiClientsPageAPI: React.FC<Props> = ({ children }) => {
  const [page, setPage] = React.useState(0)
  const [perPage, setPerPage] = React.useState(INITIAL_PER_PAGE)

  const { data, networkStatus, refetch } = useQuery(API_CLIENTS_LIST, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      pagination: {
        pageSkip: page * perPage,
        pageTake: perPage
      }
    }
  })

  const [createMutation] = useMutation(API_CLIENT_CREATE)
  const [revokeMutation] = useMutation(API_CLIENT_REVOKE)
  const [updateMutation] = useMutation(API_CLIENT_UPDATE)

  const apiClients = get(data, 'apiClients.data', [])
  const totalCount = get(data, 'apiClients.pagination.totalCount', 0)

  const handleSetPage = (value: number) => setPage(value)

  const handleSetPerPage = (value: number) => {
    setPage(0)
    setPerPage(value)
  }

  const createApiClient = async (input: CreateApiClientInput): Promise<IApiClientCreatedCredentials> => {
    const { data } = await createMutation({
      variables: {
        input: {
          name: input.name,
          scopes: input.scopes,
          expiresInDays: input.expiresInDays,
          allowedIpRanges: input.allowedIpRanges?.length ? input.allowedIpRanges : undefined
        }
      }
    })

    await refetch()

    return {
      clientId: get(data, 'apiClientCreate.data.clientId', ''),
      secret: get(data, 'apiClientCreate.secret', '')
    }
  }

  const updateApiClient = async (input: UpdateApiClientInput): Promise<void> => {
    await updateMutation({
      variables: {
        id: input.id,
        input: {
          name: input.name,
          allowedIpRanges: input.allowedIpRanges,
          active: input.active
        }
      }
    })

    await refetch()
  }

  const revokeApiClient = async (id: string): Promise<void> => {
    await revokeMutation({ variables: { id } })
    await refetch()
  }

  const value: IApiClientsContext = {
    page,
    perPage,
    refetch,
    totalCount,
    handleSetPage,
    handleSetPerPage,
    createApiClient,
    updateApiClient,
    revokeApiClient,
    data: apiClients,
    isLoading: networkStatus === 1,
    isRefetching: networkStatus === 2 || networkStatus === 4
  }

  return <ApiClientsPageContext.Provider value={value}>{children}</ApiClientsPageContext.Provider>
}

export default ApiClientsPageAPI
